import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../atoms/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Link } from '../atoms/Link';
import { Divider } from '../atoms/Divider';
import { FormField } from '../molecues/FormField';
import { SocialButton } from '../molecues/SocialButton';
import { useAuthContext } from '../../contexts/AuthContext';
import type { ApiErrorResponse } from '../../types/api/common';

/**
 * LoginForm Component - Organism Level
 * 
 * A comprehensive login form with email/password authentication
 * and social login options.
 * 
 * Features:
 * - Email and password validation
 * - Remember me functionality
 * - Social login buttons (Facebook, GitHub, Google)
 * - Loading states
 * - Error handling
 * - Accessible form
 * - Type-safe with Zod validation
 */

export interface LoginFormProps {
  /** Callback after successful login */
  onSuccess?: () => void;
  /** Callback on login error */
  onError?: (error: ApiErrorResponse) => void;
  /** Show social login options */
  showSocialLogin?: boolean;
  /** Redirect path after login */
  redirectTo?: string;
  /** Additional class names */
  className?: string;
}

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  showSocialLogin = true,
  redirectTo = '/home',
  className = '',
}) => {
  const navigate = useNavigate();
  const { isLoading, loginAdmin, isLoggingIn } = useAuthContext();
  
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form field changes
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Call login API
      await loginAdmin(formData.email, formData.password);
      
      // Show success toast
      toast.success('Login successful! Redirecting...');
      
      // Call success callback
      onSuccess?.();
      
      navigate(redirectTo, { replace: true });
      
    } catch (error) {
      console.error('Login error:', error);
      
      const apiError = error as ApiErrorResponse;
      
      // Handle different error types
      if (apiError.error?.type === 'VALIDATION') {
        setErrors({
          general: apiError.error.code || 'Please check your input and try again',
        });
      } else if (apiError.error?.type === 'AUTHENTICATION') {
        setErrors({
          general: 'Invalid email or password. Please try again.',
        });
        toast.error('Invalid email or password');
      } else {
        setErrors({
          general: 'An error occurred during login. Please try again.',
        });
        toast.error('Login failed. Please try again.');
      }
      
      // Call error callback
      onError?.(apiError);
      
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle social login
   */
  const handleSocialLogin = (provider: string) => {
    toast.error(`${provider} login is coming soon!`);
    // TODO: Implement social login
  };

  return (
    <div className={`w-full max-w-md ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-2" noValidate>
        {/* General Error Message */}
        {/* {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          </div>
        )} */}
        
        {/* Email Field */}
        <FormField
          label="Email"
          type="email"
          name="email"
          id="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          required
          autoComplete="email"
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          }
        />
        
        {/* Password Field */}
        <FormField
          label="Password"
          type="password"
          name="password"
          id="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          required
          autoComplete="current-password"
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
        />
        
        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <Checkbox
            name="rememberMe"
            checked={formData.rememberMe}
            onChange={handleChange}
            label="Remember me"
          />
          
          <Link
            to="/auth/forgot-password"
            variant="primary"
            className="text-sm font-medium"
          >
            Forgot password?
          </Link>
        </div>
        
        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="md"
          fullWidth
          isLoading={isSubmitting || isLoading || isLoggingIn}
          className="!mt-6 uppercase tracking-wide"
        >
          Sign In
        </Button>
        
        {/* Divider */}
        {showSocialLogin && (
          <>
            <Divider className="!mt-8">or continue with</Divider>
            
            {/* Social Login Buttons */}
            <div className="grid grid-cols-3 gap-3 !mt-6">
              <SocialButton
                platform="facebook"
                iconOnly
                onClick={() => handleSocialLogin('Facebook')}
              />
              <SocialButton
                platform="github"
                iconOnly
                onClick={() => handleSocialLogin('GitHub')}
              />
              <SocialButton
                platform="google"
                iconOnly
                onClick={() => handleSocialLogin('Google')}
              />
            </div>
          </>
        )}
        
        {/* Sign Up Link */}
        <div className="text-center !mt-6">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link
              to="/auth/register"
              variant="primary"
              className="font-semibold"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;

